import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Avatar,
    Chip,
    Stack,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    Close,
    Category,
    Star,
    DateRange,
    Assignment,
    TrendingUp,
    Timer,
    Feedback,
    EmojiEvents
} from '@mui/icons-material';

const BadgeDetailsDialog = ({
    open,
    onClose,
    badge
}) => {
    if (!badge) return null;

    const getRarityColor = (rarity) => {
        const colors = {
            COMMON: { bg: '#4caf50', text: 'white' },
            RARE: { bg: '#2196f3', text: 'white' },
            EPIC: { bg: '#ff9800', text: 'white' },
            LEGENDARY: { bg: '#e91e63', text: 'white' }
        };
        return colors[rarity] || colors.COMMON;
    };

    const getCategoryIcon = (category) => {
        const icons = {
            ACHIEVEMENT: <EmojiEvents />,
            MILESTONE: <Assignment />,
            STREAK: <Timer />,
            ACCURACY: <TrendingUp />,
            SPECIAL: <Star />
        };
        return icons[category] || <EmojiEvents />;
    };

    const rarityStyle = getRarityColor(badge.rarity);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 3
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{
                        background: 'rgba(255,255,255,0.2)',
                        fontSize: '2rem',
                        width: 60,
                        height: 60
                    }}>
                        {badge.iconUrl}
                    </Avatar>
                    <Box>
                        <Typography variant="h4" fontWeight={800}>
                            {badge.name}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Badge ID: {badge.id}
                        </Typography>
                    </Box>
                </Box>
                <Button
                    onClick={onClose}
                    sx={{ color: 'white', minWidth: 'auto' }}
                >
                    <Close />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 4 }}>
                    {/* Status Banner */}
                    <Paper
                        elevation={2}
                        sx={{
                            p: 3,
                            mb: 4,
                            background: badge.isActive
                                ? 'linear-gradient(135deg, #e8f5e8, #f1f8e9)'
                                : 'linear-gradient(135deg, #ffebee, #fce4ec)',
                            border: badge.isActive
                                ? '2px solid #4caf50'
                                : '2px solid #f44336'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{
                                    color: badge.isActive ? '#2e7d32' : '#c62828'
                                }}>
                                    Badge Status: {badge.isActive ? 'Active' : 'Inactive'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {badge.isActive
                                        ? 'This badge can be earned by users'
                                        : 'This badge is currently disabled'
                                    }
                                </Typography>
                            </Box>
                            <Chip
                                label={badge.isActive ? 'ACTIVE' : 'INACTIVE'}
                                color={badge.isActive ? 'success' : 'error'}
                                sx={{ fontWeight: 700 }}
                            />
                        </Box>
                    </Paper>

                    {/* Description */}
                    <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1b5e20' }}>
                            📝 Description
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                            {badge.description}
                        </Typography>
                    </Paper>

                    <Grid container spacing={4}>
                        {/* Category and Rarity */}
                        <Grid item xs={12} md={6}>
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#1b5e20' }}>
                                    🏷️ Classification
                                </Typography>
                                <Stack spacing={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {getCategoryIcon(badge.category)}
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Category
                                            </Typography>
                                            <Chip
                                                label={badge.category}
                                                sx={{
                                                    background: 'rgba(67, 160, 71, 0.1)',
                                                    color: '#43a047',
                                                    fontWeight: 600,
                                                    fontSize: '0.9rem'
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Star />
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Rarity
                                            </Typography>
                                            <Chip
                                                label={badge.rarity}
                                                sx={{
                                                    background: rarityStyle.bg,
                                                    color: rarityStyle.text,
                                                    fontWeight: 600,
                                                    fontSize: '0.9rem'
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>

                        {/* Requirements */}
                        <Grid item xs={12} md={6}>
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#1b5e20' }}>
                                    🎯 Requirements
                                </Typography>
                                <List dense>
                                    {badge.requiredClassifications > 0 && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Assignment fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${badge.requiredClassifications} Classifications`}
                                                secondary="Number of waste items to classify"
                                            />
                                        </ListItem>
                                    )}
                                    {badge.requiredStreakDays > 0 && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Timer fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${badge.requiredStreakDays} Day Streak`}
                                                secondary="Consecutive days of activity"
                                            />
                                        </ListItem>
                                    )}
                                    {badge.requiredAccuracy > 0 && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <TrendingUp fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${badge.requiredAccuracy}% Accuracy`}
                                                secondary="Minimum accuracy percentage"
                                            />
                                        </ListItem>
                                    )}
                                    {badge.requiredFeedbacks > 0 && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Feedback fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${badge.requiredFeedbacks} Feedbacks`}
                                                secondary="Accepted corrections required"
                                            />
                                        </ListItem>
                                    )}
                                    {badge.pointsRequired > 0 && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <EmojiEvents fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${badge.pointsRequired} Points`}
                                                secondary="Champion points required"
                                            />
                                        </ListItem>
                                    )}
                                    {badge.requiredClassifications === 0 &&
                                     badge.requiredStreakDays === 0 &&
                                     badge.requiredAccuracy === 0 &&
                                     badge.requiredFeedbacks === 0 &&
                                     badge.pointsRequired === 0 && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemText
                                                primary="No specific requirements"
                                                secondary="This badge may have custom unlock conditions"
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Paper>
                        </Grid>

                        {/* Metadata */}
                        <Grid item xs={12}>
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#1b5e20' }}>
                                    📊 Metadata
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <DateRange sx={{ fontSize: 40, color: '#43a047', mb: 1 }} />
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Created Date
                                            </Typography>
                                            <Typography variant="h6" fontWeight={600}>
                                                {new Date(badge.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Category sx={{ fontSize: 40, color: '#43a047', mb: 1 }} />
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Badge Type
                                            </Typography>
                                            <Typography variant="h6" fontWeight={600}>
                                                {badge.category}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Avatar sx={{
                                                fontSize: 40,
                                                background: 'transparent',
                                                mx: 'auto',
                                                mb: 1,
                                                width: 60,
                                                height: 60
                                            }}>
                                                {badge.iconUrl}
                                            </Avatar>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Icon
                                            </Typography>
                                            <Typography variant="body2">
                                                {badge.iconUrl}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #2e7d32, #43a047)'
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BadgeDetailsDialog;
